import React, { FunctionComponent, useCallback, useState } from 'react';
import { EditFaq } from './EditFaq';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useModal } from '@/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faGrip } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { Faq } from './Faq';

interface FAQItemI {
  name: string;
  description: string;
  order: number;
}

interface FaqsI {
  faqs: FAQItemI[];
  setFaqs: (items: FAQItemI[]) => void;
}

const reorder = (list: FAQItemI[], startIndex: number, endIndex: number): FAQItemI[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result.map((item, index) => ({
    ...item,
    order: index + 1
  }));
};

export const Faqs: FunctionComponent<FaqsI> = ({ faqs, setFaqs }) => {
  const [position, setPosition] = useState<number>(-1);
  const { show, onClose, onOpen } = useModal();

  const handleModalOpen = useCallback(() => {
    setPosition(-1);
    onOpen();
  }, [onOpen]);

  const handleModalClose = useCallback(() => {
    setPosition(-1);
    onClose();
  }, [onClose]);

  const handleEdit = useCallback(
    (position: number) => {
      setPosition(position);
      onOpen();
    },
    [onOpen]
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const items = reorder(faqs, result.source.index, result.destination.index);
      setFaqs(items);
    },
    [setFaqs, faqs]
  );

  const addFaq = useCallback(
    (name: string, description: string) => {
      setFaqs([...faqs, { name, description, order: faqs.length + 1 }]);
      handleModalClose();
    },
    [faqs, setFaqs, handleModalClose]
  );

  const deleteFaq = useCallback(
    (order: number) => {
      const updatedFaqs = faqs
        .filter((_, index) => index !== order)
        .map((faq, index) => ({
          ...faq,
          order: index + 1
        }));
      setFaqs(updatedFaqs);
    },
    [faqs, setFaqs]
  );

  const editFaq = useCallback(
    (question: string, answer: string, order: number) => {
      setFaqs(
        faqs.map((faq, index) => {
          if (order === index) {
            return {
              ...faq,
              name: question,
              description: answer
            };
          }
          return faq;
        })
      );
      handleModalClose();
    },
    [faqs, setFaqs, handleModalClose]
  );

  return (
    <div className="w-full mx-auto max-w-[700px]">
      <EditFaq
        show={show}
        editFaq={editFaq}
        onClose={handleModalClose}
        faq={position > -1 ? faqs[position] : null}
        position={position}
        addFaq={addFaq}
        setPosition={setPosition}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="faqs-list">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="flex flex-col w-full mx-auto max-w-screen-md gap-y-4"
            >
              {faqs.map((item, index) => (
                <Draggable key={item.order.toString()} draggableId={item.order.toString()} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style
                      }}
                      className="w-full"
                    >
                      <article
                        {...provided.dragHandleProps}
                        className={classNames(
                          'relative pl-4 gap-2 flex items-center border hover:brightness-90',
                          'sm:p-4 p-2 rounded w-full sm:h-[80px] h-[50px]',
                          'cursor-pointer bg-white mx-auto transition duration-300',
                          {
                            'shadow-lg': snapshot.isDragging
                          }
                        )}
                      >
                        <div className="absolute -top-[1px] -left-[1px] h-[calc(100%+2px)] w-2 bg-black rounded-l" />
                        <FontAwesomeIcon
                          fixedWidth
                          icon={faGrip}
                          className="rotate-90"
                          size="sm"
                          color="#828282"
                        />
                        <div className="flex-1">
                          <Faq
                            faq={item}
                            selectPositionFaqEdit={handleEdit}
                            handlerDelete={deleteFaq}
                            position={index}
                          />
                        </div>
                      </article>
                    </div>
                  )}
                </Draggable>
              ))}
              <span className="bg-gray-100">{provided.placeholder}</span>
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <button
        onClick={handleModalOpen}
        className="mt-4 w-full flex items-center justify-center gap-2 border hover:brightness-90 sm:p-4 p-2 rounded sm:h-[80px] h-[50px] cursor-pointer bg-white mx-auto transition duration-300"
      >
        <FontAwesomeIcon icon={faCirclePlus} className="text-gray-500" />
        <span className="text-gray-700">Agregar nueva</span>
      </button>
    </div>
  );
};
